import React, { useState, useEffect, useRef } from 'react';
import { Settings2, Save, Loader2, ShieldCheck, ImageIcon, Upload, RotateCcw, Share2, Download, ClipboardCopy, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const DEFAULT_LOGO_URL = '/api/uploads/pita-logo.png';
const DEFAULT_LOGO_ALT = 'Public Issue Tracker App (PITA) logo';
const PLACEMENTS = [
  { key: 'home_hero', label: 'Homepage PITA hero', hint: 'Homepage Issue Tracker promo block' },
  { key: 'pita_page', label: 'PITA page', hint: '/issue-tracker, /map, /reports headers' },
  { key: 'share_dialog', label: 'Share dialog', hint: 'Share PITA popup image' },
  { key: 'network', label: 'PITA Network', hint: '/tracker-network and Bring This to Your Ward' },
  { key: 'support', label: 'Support PITA', hint: '/support-pita, /pita-supporters, /pita/request-access' },
  { key: 'print_assets', label: 'Print assets', hint: 'Generated PITA posters and QR posters' },
  { key: 'share_cards', label: 'Share cards', hint: 'PITA share and supporter cards' },
];
const resolveUrl = (u) => (u && u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL || ''}${u || ''}`);

const STYLE_LABELS = { navy: 'Navy (solid)', navy_grid: 'Navy with grid', navy_gradient: 'Navy gradient' };
const SIZE_LABELS = { square: 'Facebook square (1080x1080)', landscape: 'Facebook landscape (1200x630)', story: 'Story / mobile (1080x1920)' };

// Phase 76: Supporter Share Card manager. Download only; nothing auto-posts.
const ShareCardSection = () => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [replaceSize, setReplaceSize] = useState('square');
  const replaceRef = useRef(null);
  const set = (k, v) => setC((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    api.get('/admin/pita/share-card').then((r) => setC(r.data)).catch(() => toast.error('Failed to load share card settings'));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put('/admin/pita/share-card', c);
      setC((prev) => ({ ...prev, ...res.data }));
      toast.success('Share card wording saved. Generate new cards to apply it to the images.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/admin/pita/share-card/generate');
      setC((prev) => ({ ...prev, ...res.data }));
      toast.success('Share cards generated in all three sizes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  const replaceImage = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/admin/pita/share-card/replace/${replaceSize}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setC((prev) => ({ ...prev, ...res.data }));
      toast.success(`Replaced the ${SIZE_LABELS[replaceSize]} image`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Replace failed'); }
    setBusy(false);
    if (replaceRef.current) replaceRef.current.value = '';
  };

  const copyShareText = async () => {
    const { copyTextRobust } = await import('../../components/PitaCTA');
    const ok = await copyTextRobust(c.share_text || '');
    if (ok) toast.success('Share text copied');
    else toast.error('Could not copy');
  };

  if (!c) return null;
  const hasImages = Object.values(c.images || {}).some(Boolean);

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Share2 className="h-5 w-5 text-[#1a2744]" />
          <h2 className="font-serif font-bold text-lg text-[#1a2744]">Supporter Share Card</h2>
        </div>
        <p className="text-xs text-slate-500">
          Ready-made images supporters can download and post to Facebook or share by email. Cards are download only.
          Nothing is posted, published, or emailed automatically. Links to the card appear on Support PITA, PITA
          Supporters, and the Share PITA dialog once cards are generated.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Title</label>
            <Input value={c.title || ''} onChange={(e) => set('title', e.target.value)} className="h-11" data-testid="share-card-title" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Slogan</label>
            <Input value={c.slogan || ''} onChange={(e) => set('slogan', e.target.value)} className="h-11" data-testid="share-card-slogan" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Subtitle</label>
            <Input value={c.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} className="h-11" data-testid="share-card-subtitle" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Website</label>
            <Input value={c.website || ''} onChange={(e) => set('website', e.target.value)} className="h-11" data-testid="share-card-website" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Disclaimer</label>
            <Input value={c.disclaimer || ''} onChange={(e) => set('disclaimer', e.target.value)} className="h-11" data-testid="share-card-disclaimer" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Share text (copied by supporters)</label>
            <Textarea value={c.share_text || ''} onChange={(e) => set('share_text', e.target.value)} rows={5} data-testid="share-card-share-text" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Background style</label>
            <Select value={c.background_style || 'navy_grid'} onValueChange={(v) => set('background_style', v)}>
              <SelectTrigger className="h-11" data-testid="share-card-style"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STYLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="share-card-save-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save Wording
          </Button>
          <Button onClick={generate} disabled={generating} className="h-10 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="share-card-generate-btn">
            {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Generate Share Cards
          </Button>
          <Button variant="outline" onClick={copyShareText} className="h-10 font-bold" data-testid="share-card-copy-text-btn">
            <ClipboardCopy className="h-4 w-4 mr-1.5" /> Copy Share Text
          </Button>
        </div>

        {hasImages && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-sm font-bold text-[#1a2744] mb-3">Active cards {c.generated_at && <span className="font-normal text-xs text-slate-400">(generated {new Date(c.generated_at).toLocaleString()})</span>}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(SIZE_LABELS).map(([size, label]) => (
                c.images?.[size] ? (
                  <div key={size} className="rounded-xl border border-slate-200 p-2.5" data-testid={`share-card-preview-${size}`}>
                    <img src={resolveUrl(c.images[size])} alt={`PITA share card, ${label}`} className="rounded-lg w-full h-32 object-cover border border-slate-100" />
                    <p className="text-[11px] text-slate-500 mt-1.5 font-bold">{label}</p>
                    <Button asChild variant="outline" size="sm" className="h-8 mt-1.5 w-full font-bold" data-testid={`share-card-download-${size}`}>
                      <a href={resolveUrl(c.images[size])} download={`pita-share-card-${size}.png`}>
                        <Download className="h-3.5 w-3.5 mr-1" /> Download
                      </a>
                    </Button>
                  </div>
                ) : null
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-2 mt-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Replace image for</label>
                <Select value={replaceSize} onValueChange={setReplaceSize}>
                  <SelectTrigger className="h-10 w-64" data-testid="share-card-replace-size"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SIZE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <input ref={replaceRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => replaceImage(e.target.files?.[0])} data-testid="share-card-replace-input" />
              <Button variant="outline" disabled={busy} onClick={() => replaceRef.current?.click()} className="h-10 font-bold" data-testid="share-card-replace-btn">
                <Upload className="h-4 w-4 mr-1.5" /> Replace Image
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminPitaSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const setPlacement = (k, v) => setS((prev) => ({ ...prev, logo_placements: { ...(prev.logo_placements || {}), [k]: v } }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'pita');
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('logo_url', res.data.url);
      toast.success('Logo uploaded. Click Save PITA Settings to make it active.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  useEffect(() => {
    api.get('/admin/pita/settings').then((r) => setS(r.data)).catch(() => toast.error('Failed to load PITA settings'));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put('/admin/pita/settings', s);
      setS(res.data);
      toast.success('PITA settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  if (!s) return <p className="text-sm text-slate-400 py-6">Loading…</p>;

  return (
    <div className="max-w-3xl" data-testid="admin-pita-settings-page">
      <div className="flex items-center gap-3 mb-1">
        <Settings2 className="h-6 w-6 text-[#1a2744]" />
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">PITA Settings</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Notification address, page toggles, and share wording for the Public Issue Tracker App (PITA).</p>

      <div className="space-y-5">
        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-serif font-bold text-lg text-[#1a2744]">Notifications</h2>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Admin notification email</label>
              <Input value={s.notification_email} onChange={(e) => set('notification_email', e.target.value)} className="h-11" data-testid="pita-settings-notification-email" />
              <p className="text-xs text-slate-400 mt-1">Every new PITA request or supporter submission sends one notification here. If SMTP is unavailable it is queued in the Notification Log instead.</p>
            </div>
            <div className="rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/10 p-3.5 flex gap-2.5" data-testid="pita-settings-locked-note">
              <ShieldCheck className="h-4 w-4 text-[#7A5C12] shrink-0 mt-0.5" />
              <p className="text-xs text-slate-700 leading-relaxed">
                <strong>Locked safety defaults:</strong> requesters and supporters are never emailed automatically, member
                accounts are never created automatically, and every submission starts private, needs review, and
                unpublished. These cannot be switched on from settings.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-serif font-bold text-lg text-[#1a2744]">Public pages</h2>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1a2744]">Request access form</p>
                <p className="text-xs text-slate-400">/pita/request-access</p>
              </div>
              <Switch checked={s.request_page_enabled} onCheckedChange={(v) => set('request_page_enabled', v)} data-testid="pita-settings-request-toggle" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1a2744]">Support PITA form</p>
                <p className="text-xs text-slate-400">/support-pita</p>
              </div>
              <Switch checked={s.support_page_enabled} onCheckedChange={(v) => set('support_page_enabled', v)} data-testid="pita-settings-support-toggle" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1a2744]">Public supporters page</p>
                <p className="text-xs text-slate-400">/pita-supporters</p>
              </div>
              <Switch checked={s.supporters_page_enabled} onCheckedChange={(v) => set('supporters_page_enabled', v)} data-testid="pita-settings-supporters-toggle" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-[#1a2744]" />
              <h2 className="font-serif font-bold text-lg text-[#1a2744]">PITA logo</h2>
            </div>
            <p className="text-xs text-slate-500">The active logo appears across PITA pages, dialogs, and print assets based on the placement toggles below.</p>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-3 h-32 w-32 flex items-center justify-center shrink-0">
                <img
                  src={resolveUrl(s.logo_url)}
                  alt={s.logo_alt || DEFAULT_LOGO_ALT}
                  className="max-h-full max-w-full object-contain"
                  data-testid="pita-settings-logo-preview"
                />
              </div>
              <div className="flex-1 min-w-0 space-y-3 w-full">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Active logo URL</label>
                  <Input value={s.logo_url || ''} onChange={(e) => set('logo_url', e.target.value)} className="h-11" data-testid="pita-settings-logo-url" />
                  <p className="text-xs text-slate-400 mt-1">Default: {DEFAULT_LOGO_URL}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Logo alt text</label>
                  <Input value={s.logo_alt || ''} onChange={(e) => set('logo_alt', e.target.value)} className="h-11" data-testid="pita-settings-logo-alt" />
                  <p className="text-xs text-slate-400 mt-1">Default: {DEFAULT_LOGO_ALT}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="pita-settings-logo-file-input" />
                  <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="h-10 font-bold" data-testid="pita-settings-logo-upload-btn">
                    {uploading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4 mr-1.5" /> Upload replacement logo</>}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { set('logo_url', DEFAULT_LOGO_URL); set('logo_alt', DEFAULT_LOGO_ALT); }} className="h-10 font-bold" data-testid="pita-settings-logo-reset-btn">
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Reset to default
                  </Button>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100">
              <p className="text-sm font-bold text-[#1a2744] mb-3">Where the logo appears</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PLACEMENTS.map((p) => (
                  <div key={p.key} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#1a2744]">{p.label}</p>
                      <p className="text-xs text-slate-400">{p.hint}</p>
                    </div>
                    <Switch
                      checked={s.logo_placements?.[p.key] !== false}
                      onCheckedChange={(v) => setPlacement(p.key, v)}
                      data-testid={`pita-settings-placement-${p.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <ShareCardSection />

        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-serif font-bold text-lg text-[#1a2744]">Share wording & button labels</h2>
            <div className="flex items-start gap-4">
              <img
                src={s.share_image_url && s.share_image_url.startsWith('http') ? s.share_image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${s.share_image_url || ''}`}
                alt="PITA share image preview"
                className="h-24 w-24 rounded-xl object-contain border border-slate-200 bg-white shrink-0"
                data-testid="pita-settings-share-image-preview"
              />
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-bold text-slate-600 mb-1">PITA logo / share image URL</label>
                <Input value={s.share_image_url} onChange={(e) => set('share_image_url', e.target.value)} className="h-11" data-testid="pita-settings-share-image" />
                <p className="text-xs text-slate-400 mt-1">Shown in the Share PITA dialog and on PITA pages. Upload a new image in Admin → Media, then paste its URL here to replace the logo.</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Share title</label>
              <Input value={s.share_title} onChange={(e) => set('share_title', e.target.value)} className="h-11" data-testid="pita-settings-share-title" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Share message</label>
              <Textarea rows={4} value={s.share_message} onChange={(e) => set('share_message', e.target.value)} data-testid="pita-settings-share-message" />
              <p className="text-xs text-slate-400 mt-1">The tagline "Report it. Map it. Track it. Get answers." and the disclaimer are always included automatically.</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Share destination link</label>
              <Input value={s.share_destination} onChange={(e) => set('share_destination', e.target.value)} className="h-11" data-testid="pita-settings-share-destination" />
              <p className="text-xs text-slate-400 mt-1">Where shared links point. Default: /support-pita</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Share button label</label>
                <Input value={s.share_cta_label} onChange={(e) => set('share_cta_label', e.target.value)} className="h-11" data-testid="pita-settings-share-label" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Support button label</label>
                <Input value={s.support_cta_label} onChange={(e) => set('support_cta_label', e.target.value)} className="h-11" data-testid="pita-settings-support-label" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Embed button label</label>
                <Input value={s.embed_cta_label} onChange={(e) => set('embed_cta_label', e.target.value)} className="h-11" data-testid="pita-settings-embed-label" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Supporters button label</label>
                <Input value={s.supporters_cta_label} onChange={(e) => set('supporters_cta_label', e.target.value)} className="h-11" data-testid="pita-settings-supporters-label" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Button onClick={save} disabled={busy} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="pita-settings-save-btn">
          {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-1.5" /> Save PITA Settings</>}
        </Button>
      </div>
    </div>
  );
}
